import React, { useState, useEffect, useRef } from "react";
import { Consts } from "../pages/consts";
import { getDiseaseName } from "../drugs/utils";
import { useTranslation } from "react-i18next";
import {filterIcd10Data} from "../pages/utils";

const TreeNode = ({
                    node,
                    level = 0,
                    selectedNode,
                    setSelectedNode,
                    patientDiseases,
                    onSetPatientDiseases,
                    searchQuery,
                    forcedOpenNodes,
                    closeDialog,
                    diseasesToShow,
                    setAlreadyAddedError,
                    firstMatchScrolledRef
                  }) => {
  const [isOpen, setIsOpen] = useState(forcedOpenNodes?.includes(node?.icd10));
  const hasChildren = node?.children && node?.children?.length > 0;
  const isCategory = node?.kind === Consts.CATEGORY;

  const matchesSearch = searchQuery
    ? getDiseaseName(node)?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || node?.icd10?.toLowerCase()?.includes(searchQuery.toLowerCase())
    : false;

  const nodeRef = useRef(null);

  useEffect(() => {
    setIsOpen(forcedOpenNodes?.includes(node?.icd10));

    if (matchesSearch && nodeRef?.current && !firstMatchScrolledRef?.current) {
      nodeRef?.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      firstMatchScrolledRef.current = true;
    }
  }, [forcedOpenNodes, matchesSearch]);

  const toggleOpen = () => {
    if (hasChildren) {
      setIsOpen(!isOpen);
    }

    setSelectedNode(node);
  };

  const handleDiseaseSelection = (event) => {
    let exists = false;

    onSetPatientDiseases((prevDiseases) => {
      let updatedSelection;

      if (event.target.checked) {
        const found = diseasesToShow?.filter(d => d.icd10 === node?.icd10);

        if (found?.length > 0) {
          exists = true;
          updatedSelection = prevDiseases;
        } else {
          const patDisease = {
            begin: (new Date()).toISOString()?.split('T')[0],
            category: node?.kind,
            icd10: node?.icd10,
            icd10name: getDiseaseName(node),
            parent_icd10_code: node?.parent_icd10_code,
            snomed: node?.snomed ? node?.snomed : "",
          }

          updatedSelection = [...prevDiseases?.filter((disease) => disease?.icd10 !== node?.parent_icd10_code), patDisease];
        }
      } else {
        updatedSelection = prevDiseases?.filter((disease) => disease?.icd10 !== node?.icd10);
      }

      return updatedSelection;
    });

    setAlreadyAddedError(exists);
    closeDialog();
  };

  const checkSelected = (disease) => {
    const selectedDiseases = patientDiseases?.filter(
      (d) => disease?.icd10 === d?.icd10,
    );

    return selectedDiseases?.length > 0;
  };

  return (
    <div
      className="w3-border-left w3-margin-left"
      style={{ paddingLeft: level * 20 }}
      ref={nodeRef}
    >
      <div
        onClick={toggleOpen}
        className={`w3-padding w3-round w3-large "w3-text-blue w3-large" ${matchesSearch ? "w3-text-blue" : "w3-text-dark-gray"}`}
        style={{ cursor: "pointer", backgroundColor: isOpen ? "#f1f1f1" : "transparent" }}
      >
        {isCategory && (
          <input
            type="checkbox"
            className="w3-margin-right"
            onChange={handleDiseaseSelection}
            checked={checkSelected(node)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
        {hasChildren && <span>{isOpen ? "▼" : "▶"}</span>} {getDiseaseName(node)} {node?.icd10 && `(${node?.icd10})`}
      </div>

      {isOpen && hasChildren && (
        <div>
          {filterIcd10Data(node?.children)?.map((child) => (
            <TreeNode
              key={child.icd10}
              node={child}
              level={level + 1}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
              patientDiseases={patientDiseases}
              onSetPatientDiseases={onSetPatientDiseases}
              searchQuery={searchQuery}
              forcedOpenNodes={forcedOpenNodes}
              closeDialog={closeDialog}
              diseasesToShow={diseasesToShow}
              setAlreadyAddedError={setAlreadyAddedError}
              firstMatchScrolledRef={firstMatchScrolledRef}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Icd10Tree = ({ data, patientDiseases, onSetPatientDiseases, closeDialog, diseasesToShow, setAlreadyAddedError }) => {
  const { t: t} = useTranslation();
  const [selectedNode, setSelectedNode] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [forcedOpenNodes, setForcedOpenNodes] = useState([]);
  const firstMatchScrolledRef = useRef(false);

  const handleSearch = () => {
    setForcedOpenNodes([]);
    setSearchQuery(searchTerm);
    setSearchTerm("");
    firstMatchScrolledRef.current = false;
  };

  useEffect(() => {
    if (searchQuery) {
      const newForcedOpenNodes = getOpenNodes(data, searchQuery);
      setForcedOpenNodes(newForcedOpenNodes);
    }
  }, [searchQuery, data]);

  const getOpenNodes = (nodes, query) => {
    let openNodes = new Set();

    const searchTree = (node, parentMatched = false) => {
      const isMatch =
        getDiseaseName(node)?.toLowerCase()?.includes(query?.toLowerCase()) ||
        node?.icd10?.toLowerCase()?.includes(query?.toLowerCase());

      let shouldOpen = isMatch || parentMatched;

      if (node?.children) {
        let childHasMatch = false;

        node?.children?.forEach((child) => {
          if (searchTree(child, shouldOpen)) {
            childHasMatch = true;
          }
        });

        if (childHasMatch) {
          shouldOpen = true;
        }
      }

      if (shouldOpen) {
        openNodes.add(node?.icd10);
      }

      return shouldOpen;
    };

    nodes?.forEach((node) => searchTree(node));
    return Array.from(openNodes);
  };

  return (
    <div>
      <div className="w3-row">
        <div className="w3-col s9">
          <input
            className="w3-input w3-border"
            type={Consts.TEXT}
            placeholder={t("search_for_disease")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w3-col s3">
          <button className="w3-button w3-blue w3-margin-left" onClick={handleSearch}>
            {t("search")}
          </button>
        </div>
      </div>
      {filterIcd10Data(data)?.map((node) => {
        return (<TreeNode
          key={node?.icd10}
          node={node}
          level={ 0 }
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          patientDiseases={patientDiseases}
          onSetPatientDiseases={onSetPatientDiseases}
          searchQuery={searchQuery}
          forcedOpenNodes={forcedOpenNodes}
          closeDialog={closeDialog}
          diseasesToShow={diseasesToShow}
          setAlreadyAddedError={setAlreadyAddedError}
          firstMatchScrolledRef={firstMatchScrolledRef}
        />);
      })}
    </div>
  );
};

export default Icd10Tree;